import { Formik, FormikHelpers } from 'formik';

import { AuthSignInPayload } from 'modules/auth/domain/interfaces/auth-sign-in-payload';
import UDFormInput from 'ud-form/components/input';
import UDInputPhone from 'ud-form/components/input/phone';
import UDButton from 'ud-ui/components/button';
import { useTranslation } from 'react-i18next';
import { theme } from 'styles/theme/theme';
import { SchemaOf } from 'yup';
import { useCallback } from 'react';
import { useCountdown } from 'shared/hooks/use-countdown';

type SignInFormProps = {
  initialValues: {
    username: string;
    password: string;
  };
  isLoading: boolean;
  onSubmit: (values: AuthSignInPayload, helpers: FormikHelpers<AuthSignInPayload>) => void;
  validationSchema?: SchemaOf<AuthSignInPayload>;
  onUsernameChange?: (value: string) => void; // Новый проп для обработки изменения телефона
  onResendCode?: () => void; // Новый проп для обработки повторной отправки кода
};

const SignInForm = (props: SignInFormProps) => {
  const { t } = useTranslation();

  // Используем хук useCountdown для управления таймером
  const {
    seconds: resendTimeout,
    isActive: isResendDisabled,
    start: startResendTimer,
  } = useCountdown({
    initialSeconds: 30,
    autoStart: true, // Запускаем таймер при монтировании компонента
  });

  // Обработчик нажатия на кнопку повторной отправки кода
  const handleResendCode = useCallback(() => {
    if (props.onResendCode) {
      props.onResendCode();
    }
    // Запускаем таймер заново
    startResendTimer(30);
  }, [props.onResendCode, startResendTimer]);

  return (
    <Formik
      initialValues={props.initialValues}
      onSubmit={props.onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={props.validationSchema}
    >
      {(formProps) => {
        return (
          <form onSubmit={formProps.handleSubmit}>
            <UDInputPhone
              style={{
                color: theme.colors.DarkBlue,
              }}
              name="username"
              label={t('signIn.phoneInputLabel')}
              placeholder={t('signIn.phoneInputPlaceholder')}
              wrapperProps={{ className: 'mb-3' }}
              value={formProps.values.username}
              onChange={(value) => {
                formProps.setFieldValue('username', value);
                // Вызываем обработчик изменения телефона, если он передан
                if (props.onUsernameChange) {
                  props.onUsernameChange(value);
                }
              }}
            />
            <UDFormInput
              autoFocus
              autoComplete="off"
              name="password"
              type="password"
              placeholder={t('signIn.passwordInputPlaceholder')}
              containerProps={{ className: 'mb-4.5' }}
            />
            <UDButton
              variant="primary"
              type="submit"
              className="w-full mb-2.5"
              style={{ fontFamily: 'SF Pro Display', fontWeight: 300 }}
              loading={props.isLoading}
            >
              {t('signIn.signInButtonLabel')}
            </UDButton>
            <UDButton
              variant="primary"
              type="button"
              className="w-full"
              style={{ fontFamily: 'SF Pro Display', fontWeight: 300 }}
              loading={props.isLoading && !isResendDisabled}
              disabled={isResendDisabled}
              onClick={handleResendCode}
            >
              {isResendDisabled
                ? `${t('signIn.sendTheCodeAgainButtonLabel')} (${resendTimeout})`
                : t('signIn.sendTheCodeAgainButtonLabel')}
            </UDButton>
          </form>
        );
      }}
    </Formik>
  );
};

export default SignInForm;
